import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import AdminApi from 'api/AdminApi';
import DiseaseApi from 'api/DiseaseApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';

import { loadAvailableDiseasesSuccess } from 'diseases/diseases.slice';
import { fetchAllUserGroups, fetchAllUserTypes } from 'user/user.saga';

import {
  finishAdminLoading,
  setCustomDiseaseGroups,
  setCustomDiseaseRelevance,
  setEmailTypes,
  setUsers,
  startAdminLoading,
  userSaveSuccess,
} from './admin.slice';

export type EditUserPayload = { id: string; data: dto.ModifyUserModel };
export type UpdateDiseaseRelevancePayload = { id: string; data: dto.DiseaseRelevanceSettingsModel };
export type EditUserTypePayload = { id: string; data: dto.ModifyUserTypeModel };
export type EditUserGroupPayload = { id: number; data: dto.UserGroupModel };
export type EditDiseaseGroupPayload = { id: number; data: dto.UpdateCustomDiseaseGroupModel };

export const fetchUsers = createAction('admin/fetchUsers');
export const fetchEmailTypes = createAction('admin/fetchEmailTypes');
export const fetchDiseaseGroups = createAction('admin/fetchDiseaseGroups');
export const fetchDiseaseRelevanceAndDiseases = createAction(
  'admin/fetchDiseaseRelevanceAndDiseases'
);

export const createUser = createAction<dto.ModifyUserModel>('admin/createUser');
export const editUser = createAction<EditUserPayload>('admin/editUser');
export const toggleUserActive = createAction<dto.UserModel>('admin/toggleUserActive');

export const updateDiseaseRelevance = createAction<UpdateDiseaseRelevancePayload>(
  'admin/updateDiseaseRelevance'
);

export const createUserType = createAction<dto.ModifyUserTypeModel>('admin/createUserType');
export const editUserType = createAction<EditUserTypePayload>('admin/editUserType');
export const deleteUserType = createAction<string>('admin/deleteUserType');

export const createUserGroup = createAction<dto.UserGroupModel>('admin/createUserGroup');
export const editUserGroup = createAction<EditUserGroupPayload>('admin/editUserGroup');
export const deleteUserGroup = createAction<number>('admin/deleteUserGroup');

export const createDiseaseGroup = createAction<dto.CreateCustomDiseaseGroupModel>(
  'admin/createDiseaseGroup'
);
export const editDiseaseGroup = createAction<EditDiseaseGroupPayload>('admin/editDiseaseGroup');
export const deleteDiseaseGroup = createAction<number>('admin/deleteDiseaseGroup');

export function* fetchUsersSaga() {
  try {
    yield put(startAdminLoading());
    const res = yield call(AdminApi.getUsers);
    yield put(setUsers(res));
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error fetching users',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* fetchEmailTypesSaga() {
  try {
    yield put(startAdminLoading());
    const res = yield call(AdminApi.getEmailTypes);
    yield put(setEmailTypes(res));
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error fetching email types',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* fetchDiseaseGroupsSaga() {
  try {
    yield put(startAdminLoading());
    const res = yield call(AdminApi.getCustomDiseaseGroups);
    yield put(setCustomDiseaseGroups(res));
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error fetching disease groups',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* createUserSaga(action: PayloadAction<dto.ModifyUserModel>) {
  try {
    yield put(startAdminLoading());
    const res: dto.UserModel = yield call(AdminApi.createUser, action.payload);
    yield put(userSaveSuccess(res));
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error creating user' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* editUserSaga(action: PayloadAction<EditUserPayload>) {
  try {
    yield put(startAdminLoading());
    const res: dto.UserModel = yield call(
      AdminApi.updateUser,
      action.payload.id,
      action.payload.data
    );
    yield put(userSaveSuccess(res));
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error updating user' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* toggleUserActiveSaga(action: PayloadAction<dto.UserModel>) {
  try {
    yield put(startAdminLoading());

    if (action.payload.isActive) yield call(AdminApi.deactivateUser, action.payload.id);
    else yield call(AdminApi.reactivateUser, action.payload.id);

    const res = yield call(AdminApi.getUser, action.payload.id);

    yield put(userSaveSuccess(res));
  } catch (e) {
    const actionType = action.payload.isActive ? 'deactivating' : 'reactivating';
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: `Error ${actionType} user` })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}
export function* fetchDiseaseRelevanceAndDiseasesSaga() {
  try {
    yield put(startAdminLoading());
    const res = yield all([
      call(DiseaseApi.getAllDiseases),
      call(AdminApi.getDiseaseRelevanceCustomGroups),
    ]);

    yield put(loadAvailableDiseasesSuccess(res[0]));
    yield put(setCustomDiseaseRelevance(res[1]));
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error fetching diseases' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* updateDiseaseRelevanceSaga(action: PayloadAction<UpdateDiseaseRelevancePayload>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.updateDiseaseRelevance, action.payload.id, action.payload.data);
    yield put(fetchDiseaseRelevanceAndDiseases());
    yield put(
      showNotification({ type: 'SHOW_SUCCESS_NOTIFICATION', payload: 'Update successful' })
    );
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error updating disease relevance',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* createUserTypeSaga(action: PayloadAction<dto.ModifyUserTypeModel>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.createUserType, action.payload);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully created a new user type',
      })
    );
    yield put(fetchAllUserTypes());
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error creating a new user type',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* editUserTypeSaga(action: PayloadAction<EditUserTypePayload>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.updateUserType, action.payload.id, action.payload.data);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully updated user type',
      })
    );
    yield put(fetchAllUserTypes());
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error saving changes' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* deleteUserTypeSaga(action: PayloadAction<string>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.deleteUserType, action.payload);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully removed user type',
      })
    );
    yield put(fetchAllUserTypes());
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error deleting user type' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* createUserGroupSaga(action: PayloadAction<dto.UserGroupModel>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.createUserGroup, action.payload);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully created a new user group',
      })
    );
    yield put(fetchAllUserGroups());
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error creating a new user group',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* editUserGroupSaga(action: PayloadAction<EditUserGroupPayload>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.updateUserGroup, action.payload.id, action.payload.data);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully updated user group',
      })
    );
    yield put(fetchAllUserGroups());
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error saving changes' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* deleteUserGroupSaga(action: PayloadAction<number>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.deleteUserGroup, action.payload);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully removed user group',
      })
    );
    yield put(fetchAllUserGroups());
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error deleting user group' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* createDiseaseGroupSaga(action: PayloadAction<dto.UserGroupModel>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.createDiseaseGroup, action.payload);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully created a new disease group',
      })
    );
    yield put(fetchDiseaseGroups());
  } catch (e) {
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'Error creating a new disease group',
      })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* editDiseaseGroupSaga(action: PayloadAction<EditDiseaseGroupPayload>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.updateDiseaseGroup, action.payload.id, action.payload.data);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully updated disease group',
      })
    );
    yield put(fetchDiseaseGroups());
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error saving changes' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

export function* deleteDiseaseGroupSaga(action: PayloadAction<number>) {
  try {
    yield put(startAdminLoading());
    yield call(AdminApi.deleteDiseaseGroup, action.payload);
    yield put(
      showNotification({
        type: 'SHOW_SUCCESS_NOTIFICATION',
        payload: 'Successfully removed disease group',
      })
    );
    yield put(fetchDiseaseGroups());
  } catch (e) {
    yield put(
      showNotification({ type: 'SHOW_ERROR_NOTIFICATION', payload: 'Error deleting disease group' })
    );
  } finally {
    yield put(finishAdminLoading());
  }
}

function* adminSaga() {
  yield takeLatest(fetchUsers, fetchUsersSaga);
  yield takeLatest(fetchEmailTypes, fetchEmailTypesSaga);
  yield takeLatest(fetchDiseaseGroups, fetchDiseaseGroupsSaga);
  yield takeLatest(fetchDiseaseRelevanceAndDiseases, fetchDiseaseRelevanceAndDiseasesSaga);

  yield takeLatest(createUser, createUserSaga);
  yield takeLatest(editUser, editUserSaga);
  yield takeLatest(toggleUserActive, toggleUserActiveSaga);

  yield takeLatest(updateDiseaseRelevance, updateDiseaseRelevanceSaga);

  yield takeLatest(createUserType, createUserTypeSaga);
  yield takeLatest(editUserType, editUserTypeSaga);
  yield takeLatest(deleteUserType, deleteUserTypeSaga);

  yield takeLatest(createUserGroup, createUserGroupSaga);
  yield takeLatest(editUserGroup, editUserGroupSaga);
  yield takeLatest(deleteUserGroup, deleteUserGroupSaga);

  yield takeLatest(createDiseaseGroup, createDiseaseGroupSaga);
  yield takeLatest(editDiseaseGroup, editDiseaseGroupSaga);
  yield takeLatest(deleteDiseaseGroup, deleteDiseaseGroupSaga);
}

export default adminSaga;
